/**
 * @internal
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
/**
 * @private
 *
 * Trigger when close to the edges of a scrollable item.
 *
 * @param offset
 */
export default function useScrollEdges(offset = 10) {
    const { isRTL } = useDirection();
    const [reachedLeft, setReachedLeft] = useState(isRTL ? false : true);
    const [reachedRight, setReachedRight] = useState(isRTL ? true : false);
    const [reachedTop, setReachedTop] = useState(true);
    const [reachedBottom, setReachedBottom] = useState(false);
    const scrollEdgesRef = useRef(null);
    const calculate = useCallback((container, initial = false) => {
        const scrollFromRight = container.scrollWidth - container.clientWidth - container.scrollLeft;
        // Needed for RTL calculation
        const scrollFromLeft = container.scrollWidth - container.clientWidth + container.scrollLeft;
        const scrollFromBottom = container.scrollHeight - container.clientHeight - container.scrollTop;
        const atRight = isRTL ? container.scrollLeft > -offset : scrollFromRight < offset;
        if (reachedRight != atRight) {
            setReachedRight(atRight);
        }
        const atLeft = isRTL ? scrollFromLeft < offset : container.scrollLeft < offset;
        if (reachedLeft != atLeft) {
            setReachedLeft(atLeft);
        }
        const atBottom = scrollFromBottom < offset;
        if (reachedBottom != atBottom) {
            setReachedBottom(atBottom);
        }
        const atTop = container.scrollTop < offset;
        if (reachedTop != atTop) {
            setReachedTop(atTop);
        }
        if (initial && container.scrollWidth === container.clientWidth) {
            // items fit inside the parent container
            setReachedLeft(true);
            setReachedRight(true);
        }
    }, [offset, isRTL, reachedRight, reachedLeft, reachedBottom, reachedTop]);
    const handleScroll = useCallback((e) => {
        calculate(e.currentTarget);
    }, [calculate]);
    useEffect(() => {
        // first render
        if (scrollEdgesRef.current !== null) {
            calculate(scrollEdgesRef.current, true);
        }
    }, [calculate]);
    return { handleScroll, scrollEdgesRef, reachedLeft, reachedRight, reachedBottom, reachedTop };
}
